/**
 * Landing pages publicitaires — pilotées par paramètre d'URL.
 *
 * Route unique : /lp/?id={variante}&ville={ville}
 *
 * Tout le trafic Google Ads converge sur un seul composant. Le contenu
 * est résolu depuis ce dictionnaire, jamais depuis la valeur brute de l'URL.
 *
 * Règle de sécurité : les paramètres `id` et `ville` servent UNIQUEMENT de
 * clés de recherche. Ils ne sont jamais affichés, concaténés ni injectés
 * dans le DOM. Une clé inconnue retombe sur une valeur par défaut.
 */

/* ------------------------------------------------------------------ */
/* Villes                                                              */
/* ------------------------------------------------------------------ */

export interface Ville {
  /** Clé canonique, celle attendue dans ?ville= */
  key: string;
  /** Nom affichable, seule valeur qui atteint le DOM. */
  nom: string;
  /** Région, utilisée dans l'accroche du présentiel. */
  region: string;
}

export const villes: Record<string, Ville> = {
  paris:     { key: "paris",     nom: "Paris",     region: "Île-de-France" },
  lyon:      { key: "lyon",      nom: "Lyon",      region: "Auvergne-Rhône-Alpes" },
  marseille: { key: "marseille", nom: "Marseille", region: "Provence" },
  toulouse:  { key: "toulouse",  nom: "Toulouse",  region: "Occitanie" },
  bordeaux:  { key: "bordeaux",  nom: "Bordeaux",  region: "Nouvelle-Aquitaine" },
  lille:     { key: "lille",     nom: "Lille",     region: "Hauts-de-France" },
  nantes:    { key: "nantes",    nom: "Nantes",    region: "Pays de la Loire" },
};

/**
 * Résout une clé de ville. Retourne null si la clé est absente ou inconnue,
 * ce qui fait retomber la page sur sa version nationale.
 */
export function getVilleByKey(key: string | null | undefined): Ville | null {
  if (!key) return null;
  return villes[key.trim().toLowerCase()] ?? null;
}

/* ------------------------------------------------------------------ */
/* Variantes                                                           */
/* ------------------------------------------------------------------ */

export interface Benefit {
  title: string;
  text: string;
}

export interface FaqEntry {
  q: string;
  a: string;
}

export interface Variant {
  /** Clé courte utilisée dans l'URL : /lp/?id={id} */
  id: string;
  metaTitle: string;
  metaDescription: string;
  /** Sert à alimenter getRecommendedCourses. */
  targetKeywords: string[];
  h1: string;
  subhead: string;
  ctaLabel: string;
  benefits: Benefit[];
  opcoTitre?: string;
  opcoNote: string;
  faq: FaqEntry[];
  testimonial: { quote: string; name: string; role: string };
  /** Slug stable dans courses.ts. Le titre est résolu à l'affichage. */
  preselectedFormationSlug: string;
  courseName: string;
  exitText?: string;
  exitFAQ?: Array<{ q: string }>;
  /** Si true, un paramètre ?ville= reconnu personnalise la page. */
  supportsVille?: boolean;
}

export const variants: Record<string, Variant> = {
  /* ---------------------------------------------------------------- */
  entreprise: {
    id: "entreprise",
    metaTitle: "Formation IA en entreprise sur mesure",
    metaDescription:
      "Formez vos équipes à l'intelligence artificielle. Programmes sur mesure, présentiel ou distanciel, finançables OPCO. 1300+ professionnels formés.",
    targetKeywords: [
      "formation IA entreprise",
      "formation intelligence artificielle entreprise",
      "formation IA sur mesure",
      "formation IA équipe",
    ],
    h1: "Formation IA pour votre entreprise",
    subhead:
      "Rendez vos équipes autonomes sur l'intelligence artificielle. Programmes sur mesure et sur vos outils : ChatGPT, Copilot, Gemini, Claude. En présentiel ou à distance, finançables par votre OPCO.",
    ctaLabel: "Recevoir un programme personnalisé gratuitement",
    benefits: [
      { title: "Adaptée à vos métiers", text: "Contenu construit autour de vos cas d'usage réels, pas une formation générique." },
      { title: "Aucun prérequis technique", text: "Pensée pour des équipes opérationnelles, du débutant au manager." },
      { title: "Animée par un expert", text: "1300+ professionnels déjà formés dans des groupes comme EDF, L'Oréal ou Thales." },
      { title: "Agents IA", text: "Comment créer un agent IA qui automatise vos tâches." },
    ],
    opcoNote:
      "Cette formation est éligible à un financement par votre OPCO. Nous vous aidons à monter le dossier.",
    faq: [
      { q: "La formation est-elle adaptable à notre secteur ?", a: "Oui, chaque programme est construit sur mesure à partir de vos métiers et de vos cas d'usage." },
      { q: "Faut-il des prérequis techniques ?", a: "Non. Nos formations s'adressent à tous les profils, sans bagage technique." },
      { q: "Présentiel ou distanciel ?", a: "Les deux. Nous intervenons sur site partout en France ou à distance, selon votre préférence." },
      { q: "Est-ce finançable ?", a: "Oui, la formation est finançable par votre OPCO. Nous vous accompagnons sur le dossier." },
    ],
    testimonial: {
      quote: "Notre équipe a pu mettre en place une stratégie IA cohérente qui a transformé notre approche business. Le ROI a été immédiat.",
      name: "Sophie D.",
      role: "Directrice de l'Innovation, groupe de luxe",
    },
    preselectedFormationSlug: "etat-de-lart-de-lia-technologies-enjeux-applications",
    courseName: "Formation IA en entreprise sur mesure",
    exitText: "Cherchez-vous une formation spécifique pour l'IA dans votre secteur ?",
  },

  /* ---------------------------------------------------------------- */
  agents: {
    id: "agents",
    metaTitle: "Formation Agents IA et automatisation des processus",
    metaDescription:
      "Atelier 2 jours pour concevoir des agents IA qui automatisent vos tâches répétitives. En intra, sur vos process réels. Finançable OPCO.",
    targetKeywords: [
      "formation agents IA",
      "formation automatisation IA",
      "agent IA entreprise",
      "automatisation processus IA",
    ],
    h1: "Formation Agents IA pour automatiser vos processus",
    subhead:
      "Deux jours pour concevoir avec vos équipes des agents IA qui prennent en charge vos tâches répétitives. Sur vos process réels et vos outils, en intra-entreprise.",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Sur vos process réels", text: "L'atelier part de vos tâches chronophages, pas d'exemples génériques." },
      { title: "De l'idée au prototype", text: "Vos équipes repartent avec un premier agent fonctionnel." },
      { title: "Sans compétence technique", text: "Outils no-code et low-code, accessibles aux équipes métier." },
      { title: "Cadrage et sécurité", text: "Ce qu'on automatise, ce qu'on ne délègue pas, et comment garder le contrôle." },
    ],
    opcoNote:
      "Atelier intra-entreprise éligible à un financement OPCO. Nous montons le dossier avec vous.",
    faq: [
      { q: "Faut-il savoir coder ?", a: "Non, l'atelier s'appuie sur des outils no-code. Les profils techniques peuvent aller plus loin sur leur propre stack." },
      { q: "Quels processus peut-on automatiser ?", a: "Traitement d'emails, extraction de données, rédaction de comptes rendus, veille, relances, qualification de demandes entrantes. Nous partons de vos cas." },
      { q: "Repart-on avec quelque chose de concret ?", a: "Oui, chaque groupe construit au moins un agent fonctionnel sur un de ses propres processus." },
      { q: "Est-ce finançable ?", a: "Oui, via votre OPCO. Nous vous accompagnons sur le dossier." },
    ],
    testimonial: {
      quote: "La qualité des formateurs et l'approche pratique nous ont permis d'implémenter rapidement des solutions performantes. Nos équipes sont désormais autonomes.",
      name: "Marc L.",
      role: "DSI, entreprise de services numériques",
    },
    preselectedFormationSlug: "agents-ia-automatiser-processus",
    courseName: "Formation Agents IA et automatisation",
    exitText: "Vous vous demandez quels processus vous pourriez automatiser ?",
    exitFAQ: [
      { q: "Quels processus peut-on automatiser en premier ?" },
      { q: "Faut-il des compétences techniques ?" },
      { q: "Combien coûte l'atelier ?" },
      { q: "Peut-on l'adapter à nos outils internes ?" },
    ],
  },

  /* ---------------------------------------------------------------- */
  copilot: {
    id: "copilot",
    metaTitle: "Formation Microsoft Copilot 365 en entreprise",
    metaDescription:
      "Formation Copilot 365 en intra : Word, Excel, Teams, Outlook, PowerPoint. Une journée 100% pratique sur vos documents. Finançable OPCO.",
    targetKeywords: [
      "formation Microsoft Copilot",
      "formation Copilot 365",
      "formation Copilot entreprise",
      "formation Microsoft 365 Copilot",
    ],
    h1: "Formation Microsoft Copilot 365 pour vos équipes",
    subhead:
      "Word, Excel, Teams, Outlook, PowerPoint. Une journée pour que vos collaborateurs tirent réellement parti de Copilot sur leurs documents et leurs process.",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Sur toute la suite", text: "Word, Excel, PowerPoint, Outlook et Teams, avec les usages qui font gagner du temps." },
      { title: "Sur vos vrais documents", text: "Les exercices utilisent vos modèles et vos fichiers, pas des cas d'école." },
      { title: "Ce que Copilot ne sait pas faire", text: "Repérer les limites évite les déceptions et les erreurs coûteuses." },
      { title: "Adoption durable", text: "Des réflexes quotidiens, pas une démonstration oubliée la semaine suivante." },
    ],
    opcoNote:
      "Formation intra-entreprise éligible à un financement OPCO. Accompagnement dossier inclus.",
    faq: [
      { q: "Faut-il déjà avoir des licences Copilot ?", a: "Oui, la formation suppose que Copilot est déployé. Nous pouvons intervenir avant le déploiement sur un format de cadrage." },
      { q: "Quelles applications sont couvertes ?", a: "Word, Excel, PowerPoint, Outlook et Teams. Le dosage s'adapte aux usages de votre équipe." },
      { q: "Combien de participants ?", a: "De 5 à 200, en une ou plusieurs sessions selon la taille de vos équipes." },
      { q: "Est-ce finançable ?", a: "Oui, via votre OPCO." },
    ],
    testimonial: {
      quote: "Une formation extrêmement concrète. Nos équipes ont commencé à utiliser l'IA dès le lendemain avec des gains de temps immédiats.",
      name: "Fabien C.",
      role: "Groupe international",
    },
    preselectedFormationSlug: "microsoft-copilot-365",
    courseName: "Formation Microsoft Copilot 365",
    exitText: "Vous vous demandez ce que Copilot peut vraiment apporter à vos équipes ?",
    exitFAQ: [
      { q: "Faut-il des licences Copilot au préalable ?" },
      { q: "Pouvez-vous former une centaine d'employés ?" },
      { q: "Travaillez-vous sur nos propres documents ?" },
      { q: "Combien coûte la formation ?" },
    ],
  },

  /* ---------------------------------------------------------------- */
  rh: {
    id: "rh",
    metaTitle: "Formation IA pour les RH et DRH",
    metaDescription:
      "Formez votre équipe RH à l'IA : recrutement, tri de CV, marque employeur, automatisation. Conformité RGPD & AI Act. Finançable OPCO.",
    targetKeywords: [
      "formation IA RH",
      "formation IA pour les RH",
      "IA recrutement",
      "IA pour DRH",
    ],
    h1: "Formation IA pour les RH et les DRH",
    subhead:
      "Recrutement, tri de CV, marque employeur, automatisation des tâches : formez votre équipe RH à utiliser l'IA efficacement et en conformité (RGPD, AI Act).",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Cas d'usage RH concrets", text: "Sourcing, tri de CV, rédaction de fiches de poste, réponses candidats, onboarding." },
      { title: "Conformité RGPD & AI Act", text: "Cadrage des biais, de la non-discrimination et de la gestion des données candidats." },
      { title: "Adaptée à vos process", text: "Construite autour de votre SIRH et de vos pratiques de recrutement." },
      { title: "Agents IA", text: "Comment créer un agent IA RH qui automatise vos tâches." },
    ],
    opcoNote: "Formation éligible à un financement OPCO. Nous montons le dossier avec vous.",
    faq: [
      { q: "Quels cas d'usage RH sont couverts ?", a: "Sourcing, tri de CV, fiches de poste, suivi et communication candidats, onboarding et automatisation des tâches répétitives. Nous pouvons vous fournir un agenda détaillé sur demande." },
      { q: "Comment gérez-vous les risques de biais et de discrimination ?", a: "Un module dédié couvre les exigences de l'AI Act et les bonnes pratiques pour éviter les biais à l'embauche." },
      { q: "Présentiel ou distanciel ?", a: "Les deux. Nous intervenons sur site partout en France ou à distance, selon votre préférence." },
      { q: "Est-ce finançable ?", a: "Oui, via votre OPCO. Nous vous accompagnons sur le dossier." },
    ],
    testimonial: {
      quote: "La formation sur mesure a parfaitement répondu à nos attentes. L'accent mis sur l'éthique et la responsabilité dans l'usage de l'IA nous a particulièrement séduits.",
      name: "Nathalie P.",
      role: "DRH, groupe industriel",
    },
    preselectedFormationSlug: "ia-rh",
    courseName: "Formation IA pour les RH et DRH",
    exitText: "Vous hésitez sur comment intégrer l'IA dans votre process de recrutement ?",
    exitFAQ: [
      { q: "Quelle formation est adaptée à mon équipe RH ?" },
      { q: "Combien coûte une formation ?" },
      { q: "Abordez-vous les sujets éthiques et sécurité ?" },
      { q: "Est-ce adapté aux débutants ?" },
    ],
  },

  /* ---------------------------------------------------------------- */
  marketing: {
    id: "marketing",
    metaTitle: "Formation IA pour le Marketing",
    metaDescription:
      "Formez votre équipe marketing à l'IA : contenu, campagnes, visuels, SEO. Produisez plus et mieux tout en gardant votre marque. Intra-entreprise, OPCO.",
    targetKeywords: [
      "formation IA marketing",
      "IA génération de contenu",
      "formation IA pour le marketing",
    ],
    h1: "Formation IA pour le Marketing",
    subhead:
      "Génération de contenu, campagnes, visuels, SEO : formez votre équipe marketing à produire plus et mieux avec l'IA, sans sacrifier la qualité ni la marque.",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Production de contenu", text: "Articles, posts, emails, scripts : accélérer la création sans perdre en qualité." },
      { title: "Visuels & campagnes", text: "Générer et décliner des visuels, idéer et structurer des campagnes selon vos outils." },
      { title: "Garder le ton de marque", text: "Méthode de cadrage et de relecture pour rester fidèle à votre identité." },
      { title: "Agents IA", text: "Comment créer un agent IA marketing qui automatise vos tâches." },
    ],
    opcoNote: "Formation intra-entreprise éligible OPCO.",
    faq: [
      { q: "Quels usages marketing sont couverts ?", a: "Rédaction (articles, posts, emails), visuels, idéation de campagnes, réseaux sociaux, aide au SEO et à l'analyse." },
      { q: "L'IA va-t-elle uniformiser notre contenu ?", a: "Non : nous formons vos équipes à cadrer l'IA pour préserver votre ton de marque." },
      { q: "Est-ce adapté à une équipe interne ?", a: "Oui, la formation est conçue en intra, autour de vos outils et de vos campagnes." },
      { q: "Est-ce finançable ?", a: "Oui, via votre OPCO." },
    ],
    testimonial: {
      quote: "Une approche concrète qui a permis à nos équipes de gagner un temps précieux sur la production de contenu, tout en montant en compétence.",
      name: "Claire M.",
      role: "Directrice Marketing, groupe international",
    },
    preselectedFormationSlug: "ia-marketing",
    courseName: "Formation IA pour le Marketing",
    exitText: "Vous cherchez à optimiser votre production de contenu avec l'IA ?",
    exitFAQ: [
      { q: "Quelle formation est adaptée à mon équipe marketing ?" },
      { q: "Pouvez-vous former une centaine d'employés ?" },
      { q: "Abordez-vous les sujets éthiques et sécurité ?" },
      { q: "Pouvez-vous adapter la formation selon les départements ?" },
    ],
  },

  /* ---------------------------------------------------------------- */
  prompt: {
    id: "prompt",
    metaTitle: "Formation Prompt Engineering pour entreprises",
    metaDescription:
      "Formation prompt engineering en intra-entreprise. Apprenez à vos équipes à rédiger des prompts efficaces et fiables sur ChatGPT, Copilot, Gemini et Claude. OPCO.",
    targetKeywords: [
      "formation prompt engineering",
      "formation rédaction de prompts",
      "maîtriser les prompts en entreprise",
    ],
    h1: "Formation Prompt Engineering pour vos équipes",
    subhead:
      "Apprenez à vos collaborateurs à rédiger des prompts efficaces et à fiabiliser leurs résultats. Formation intra-entreprise, 100% orientée pratique.",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Une méthode, pas des astuces", text: "Une démarche structurée de rédaction de prompts, transposable à tous vos outils." },
      { title: "Sur vos outils", text: "Cas pratiques sur ChatGPT, Microsoft Copilot, Gemini et Claude, selon votre environnement." },
      { title: "Des résultats fiables", text: "Réduire les hallucinations, vérifier et sécuriser les sorties de l'IA." },
      { title: "Agents IA", text: "Comment créer un agent IA qui automatise vos tâches." },
    ],
    opcoNote: "Formation intra-entreprise éligible OPCO. Accompagnement dossier inclus.",
    faq: [
      { q: "Est-ce une formation pour particuliers ou pour les équipes ?", a: "Exclusivement pour les entreprises, en intra-entreprise ou en distanciel, pour vos collaborateurs et vos cas d'usage." },
      { q: "Sur quels outils travaille-t-on ?", a: "ChatGPT, Microsoft Copilot, Gemini, Claude. Nous adaptons aux outils de votre entreprise." },
      { q: "Faut-il un niveau particulier ?", a: "Non, la formation part des bases et monte rapidement en pratique." },
      { q: "Est-ce finançable ?", a: "Oui, via votre OPCO." },
    ],
    testimonial: {
      quote: "La qualité des formateurs et l'approche pratique nous ont permis d'implémenter rapidement des solutions performantes. Nos équipes sont désormais autonomes.",
      name: "Marc L.",
      role: "DSI, entreprise de services numériques",
    },
    preselectedFormationSlug: "chatgpt-avance-prompt-engineering",
    courseName: "Formation Prompt Engineering en entreprise",
    exitText: "Vous cherchez à maîtriser les bonnes pratiques du prompt engineering ?",
  },

  /* ---------------------------------------------------------------- */
  presentiel: {
    id: "presentiel",
    metaTitle: "Formation IA en présentiel dans votre entreprise",
    metaDescription:
      "Formations IA animées sur site, partout en France : Paris, Lyon, Marseille, Toulouse, Bordeaux, Lille, Nantes. Intra-entreprise, finançable OPCO.",
    targetKeywords: [
      "formation IA présentiel",
      "formation IA sur site",
      "formation IA Lyon",
      "formation IA Marseille",
      "formation IA Toulouse",
      "formation IA Bordeaux",
      "formation IA Lille",
      "formation IA Nantes",
    ],
    h1: "Formation IA en présentiel dans votre entreprise",
    subhead:
      "Nos formations IA animées sur site, partout en France. Paris, Lyon, Marseille, Toulouse, Bordeaux, Lille, Nantes. Intra-entreprise et finançable OPCO.",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Sur site, partout en France", text: "Nous nous déplaçons dans vos locaux, dans toutes les grandes villes." },
      { title: "Adaptée à votre secteur", text: "Programme calé sur vos métiers et vos cas d'usage." },
      { title: "Animée par un expert", text: "1300+ professionnels formés, 98% de satisfaction." },
      { title: "Agents IA", text: "Comment créer un agent IA qui automatise vos tâches." },
    ],
    opcoNote: "Formation en présentiel éligible à un financement OPCO.",
    faq: [
      { q: "Vous déplacez-vous dans nos locaux ?", a: "Oui, nous animons les formations sur site partout en France." },
      { q: "Quelles villes couvrez-vous ?", a: "Toutes les grandes métropoles : Paris et Île-de-France, Lyon, Marseille, Toulouse, Bordeaux, Lille, Nantes et au-delà." },
      { q: "Le distanciel est-il possible ?", a: "Oui, si vous le préférez, nous proposons aussi le format à distance." },
      { q: "Est-ce finançable ?", a: "Oui, via votre OPCO." },
    ],
    testimonial: {
      quote: "Une formation sur site qui a réuni toutes nos équipes autour des enjeux IA, avec un impact immédiat sur notre quotidien.",
      name: "Marc L.",
      role: "DSI, entreprise de services numériques",
    },
    preselectedFormationSlug: "etat-de-lart-de-lia-technologies-enjeux-applications",
    courseName: "Formation IA en présentiel en entreprise",
    exitText: "Vous hésitez entre formation en présentiel ou à distance ?",
    supportsVille: true,
  },

  /* ---------------------------------------------------------------- */
  geneve: {
    id: "geneve",
    metaTitle: "Formation IA à Genève en présentiel dans votre entreprise",
    metaDescription:
      "Formation IA et ChatGPT pour entreprises à Genève, Lausanne et en Suisse romande. Intra-entreprise, sur site ou à distance, adaptée à vos métiers.",
    targetKeywords: [
      "formation IA Genève",
      "formation IA entreprise Genève",
      "formation ChatGPT Genève",
      "formation IA Suisse romande",
      "formation IA Lausanne",
      "formation IA entreprise Suisse",
    ],
    h1: "Formation IA en présentiel dans votre entreprise à Genève",
    subhead:
      "Nos formations IA animées sur site à Genève, Lausanne et partout en Suisse romande. Intra-entreprise, personnalisées selon vos métiers et vos cas d'usage.",
    ctaLabel: "Recevoir un programme personnalisé",
    benefits: [
      { title: "Sur site en Suisse romande", text: "Nous intervenons dans vos locaux à Genève, Lausanne et dans toute la Suisse romande." },
      { title: "Adaptée à votre secteur", text: "Programme calé sur vos métiers, vos outils et vos cas d'usage." },
      { title: "Animée par un expert", text: "1300+ professionnels formés, 98% de satisfaction." },
      { title: "Agents IA", text: "Comment créer un agent IA qui automatise vos tâches." },
    ],
    opcoNote:
      "Formation IA sur site à Genève, personnalisée selon vos objectifs et les besoins de vos équipes.",
    faq: [
      { q: "Vous déplacez-vous dans nos locaux ?", a: "Oui, nous animons les formations directement dans vos bureaux à Genève, Lausanne et partout en Suisse romande." },
      { q: "La formation est-elle adaptée à notre activité ?", a: "Oui, le programme est adapté à votre secteur, vos métiers, vos outils et vos cas d'usage." },
      { q: "Faut-il déjà connaître ChatGPT ou Copilot ?", a: "Non, nos formations conviennent aussi bien aux débutants qu'aux utilisateurs souhaitant aller plus loin." },
      { q: "Le distanciel est-il possible ?", a: "Oui, les formations peuvent aussi être organisées à distance ou en format hybride." },
    ],
    testimonial: {
      quote: "Une formation extrêmement concrète. Nos équipes ont commencé à utiliser l'IA dès le lendemain avec des gains de temps immédiats.",
      name: "Fabien C.",
      role: "Groupe international",
    },
    preselectedFormationSlug: "etat-de-lart-de-lia-technologies-enjeux-applications",
    courseName: "Formation IA en entreprise à Genève",
    exitText: "Vous cherchez une formation IA adaptée à votre entreprise suisse ?",
    exitFAQ: [
      { q: "Vous déplacez-vous à Genève dans nos locaux ?" },
      { q: "Pouvez-vous former une centaine d'employés ?" },
      { q: "Abordez-vous les cas d'usage finance ou par industrie ?" },
      { q: "Pouvez-vous adapter la formation selon les départements ?" },
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Résolution                                                          */
/* ------------------------------------------------------------------ */

export const DEFAULT_VARIANT_ID = "entreprise";

/**
 * Retourne toujours une variante valide. Un visiteur venant d'une annonce
 * payante ne doit jamais tomber sur une page d'erreur.
 */
export function getVariantById(id: string | null | undefined): Variant {
  if (!id) return variants[DEFAULT_VARIANT_ID];
  return variants[id.trim().toLowerCase()] ?? variants[DEFAULT_VARIANT_ID];
}

/** Anciennes routes /lp/{slug}/ vers les nouveaux id, pour les redirections. */
export const LEGACY_SLUG_TO_ID: Record<string, string> = {
  "formation-ia-entreprise": "entreprise",
  "formation-ia-rh": "rh",
  "formation-ia-marketing": "marketing",
  "formation-prompt-engineering": "prompt",
  "formation-ia-presentiel": "presentiel",
  "formation-ia-geneve": "geneve",
};

/** Champs d'affichage après application éventuelle de la ville. */
export interface ResolvedVariant extends Variant {
  ville: Ville | null;
  canonical: string;
  /** Valeur envoyée dans le formulaire pour tracer l'origine. */
  lpSource: string;
}

/**
 * Applique la personnalisation ville à une variante qui la supporte.
 * Seuls le titre, l'accroche, le premier bénéfice et les métadonnées changent.
 * Le reste de la page est identique, sinon on recrée N pages distinctes.
 */
export function resolveVariant(
  variant: Variant,
  ville: Ville | null
): ResolvedVariant {
  const applies = Boolean(ville && variant.supportsVille);
  const base: ResolvedVariant = {
    ...variant,
    ville: applies ? ville : null,
    canonical: `https://www.upnov.com/lp/?id=${variant.id}`,
    lpSource: variant.id,
  };

  if (!applies || !ville) return base;

  return {
    ...base,
    metaTitle: `Formation IA en présentiel à ${ville.nom}`,
    metaDescription: `Formation IA animée sur site à ${ville.nom} et en ${ville.region}. Intra-entreprise, programme adapté à vos métiers, finançable OPCO.`,
    h1: `Formation IA en présentiel à ${ville.nom}`,
    subhead: `Le formateur se déplace dans vos locaux à ${ville.nom} et en ${ville.region}. Formation intra-entreprise, adaptée à vos métiers et à vos cas d'usage.`,
    benefits: [
      {
        title: `Sur site à ${ville.nom}`,
        text: `Nous nous déplaçons dans vos locaux à ${ville.nom} et dans toute la région ${ville.region}.`,
      },
      ...variant.benefits.slice(1),
    ],
    canonical: `https://www.upnov.com/lp/?id=${variant.id}&ville=${ville.key}`,
  };
}

/**
 * Point d'entrée unique pour le composant.
 * Les deux arguments viennent directement de useSearchParams.
 */
export function resolveLandingPage(
  idParam: string | null,
  villeParam: string | null
): ResolvedVariant {
  return resolveVariant(getVariantById(idParam), getVilleByKey(villeParam));
}